import {
    Injectable,
    signal
} from '@angular/core';

import {
    BehaviorSubject,
} from "rxjs";

import {
    CustomFormControl,
    CustomFormControlProps,
    CustomFormGroup,
    CustomFormGroupProps,
    KeyValueIconImage,
    ListItemGeneric
} from "@tilby/tilby-ui-lib/components/tilby-magic-form";

import {
    Rooms,
    RoomsTables,
} from "tilby-models";

import {CustomForm} from "@tilby/tilby-ui-lib/components/tilby-magic-form";
import {Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

import {
    NEW_TABLES,
    AllTablesShape,
    TableWithIcon,
    STATIC_ELEMENTS,
    AllStaticElementsShape,
    STATIC_ELEMENT_PREFIX
} from 'src/app/core';

import {TopbarTablesTotal} from "./components/topbar-tables-total";
import { ROOM_MULTIPLICATION_FACTOR } from './tables.component';
import { autobind } from 'src/app/models/decorators.model';

export interface DuplicateTableForm{
    new_name: string;
    row_elements: number;
    column_elements: number;
}

export type ShapeImage = {table?:TableWithIcon<AllTablesShape>,element?:TableWithIcon<AllStaticElementsShape>};

export type AddEditTable = {[prop in keyof Pick<RoomsTables, 'name'|'shape'|'covers'|'order_type'>]:RoomsTables[prop]}&{shapeImage:ShapeImage};

export type NewEditRoomForm = {
    name: string;
    default_pricelist: number;
    cover_type: 'default' | 'none' | 'id' | 'perc';
    cover_value_id: number | null;
    cover_value_perc: number | null;
    floor: string;
    skip_creation_dialog: 'default' | 'on' | 'off';
};

@Injectable({
    providedIn: 'root'
})
export class RoomsStateService {
    tabIndex = signal(0);
    isEditMode = signal(false,{equal:(a,b)=>a==b});
    roomForm?: CustomFormGroup<CustomForm<NewEditRoomForm>>;
    tableForm?: CustomFormGroup<CustomForm<AddEditTable>>;
    copyTableForm?: CustomFormGroup<CustomForm<{new_name:string,row_elements:number,column_elements:number}>>;
    totalsArray$= new BehaviorSubject<TopbarTablesTotal[]>([]);

    selectedRoom: number = 0;

    constructor(
        protected translateService: TranslateService,
    ) {}

    public minRoomHeightWidth(roomSelected?:Rooms){
        let tmpTable = roomSelected?.tables?.reduce((maxTable : RoomsTables | null, table) => {
            if (!maxTable) return table;
            const currentTableValue = table.height + table.ypos;
            return currentTableValue > (maxTable.height + maxTable.ypos) ? table : maxTable;
        }, null);

        const minHeight = tmpTable ? (tmpTable.height + tmpTable.ypos + 40) : 40;

        tmpTable = roomSelected?.tables?.reduce((maxTable : RoomsTables | null, table) => {
            if (!maxTable) return table;
            const currentTableValue = table.width + table.xpos;
            return currentTableValue > (maxTable.width + maxTable.xpos) ? table : maxTable;
        }, null);

        const minWidth = tmpTable ? (tmpTable.width + tmpTable.xpos + 40) : 40;

        return {minHeight,minWidth};
    }

// START - ADD_TABLE FORM
    public createAddEditTableForm(tableElementTypeChosen?: TableWithIcon<AllStaticElementsShape | AllTablesShape>, isEdit?: boolean, tableElementSelected?: RoomsTables) {
        const isTable = !tableElementTypeChosen?.shape.startsWith(STATIC_ELEMENT_PREFIX);

        const orderTypeChoices: KeyValueIconImage<string>[] = [
            'TABLES.ADD_TABLE.ORDER_TYPE_SINGLE',
            'TABLES.ADD_TABLE.ORDER_TYPE_MULTIPLE'
        ].map(type => ({
            key: type,
            value: type.toLowerCase().split('_').pop() || type
        }));

        const tableSizes: KeyValueIconImage<string>[] = tableElementTypeChosen
            ? this.getTableSizes(tableElementTypeChosen)
            : [];

        const tableElementToListItem = <T extends AllStaticElementsShape | AllTablesShape>(
            tableElement: TableWithIcon<T> | undefined,
            callback: (param: TableWithIcon<T>) => void
        ): ListItemGeneric | undefined =>
            tableElement ? {
                id: tableElement.id,
                type: 'generic',
                hideDivider: true,
                svgIcon: tableElement.icon,
                class: 'tw-cursor-pointer tw-py-8 tw-px-10 tw-flex tw-justify-center tw-items-center',
                click: () => tableElement && callback(tableElement)
            } : undefined;

        const tableShapes: Array<ListItemGeneric> = NEW_TABLES.map(table =>
            tableElementToListItem(table, this.selectTable)!
        );

        const elementsShapes: Array<ListItemGeneric> = STATIC_ELEMENTS.map(element =>
            tableElementToListItem(element, this.selectElement)!
        );

        const form = new CustomFormGroup<CustomForm<AddEditTable>>({
            name: new CustomFormControl(
                tableElementSelected?.name || '',
                { validators: [Validators.required, Validators.minLength(3), Validators.maxLength(25)] },
                {
                    ...new CustomFormControlProps(),
                    label: `TABLES.${isEdit ? 'EDIT_TABLE' : 'ADD_TABLE'}.NAME`,
                    hint: () => `${this.tableForm?.controls.name?.value?.length || 0}/25`,
                    class: 'tw-w-full',
                    positionIndex: 2
                }
            ),
            shape: new CustomFormControl(
                tableElementSelected ? tableElementSelected.shape : tableSizes[0]?.value,
                { validators: [Validators.required] },
                {
                    ...new CustomFormControlProps(),
                    label: `TABLES.${isEdit ? 'EDIT_TABLE' : 'ADD_TABLE'}.SIZES`,
                    inputType: 'select',
                    inputChoices: tableSizes,
                    class: '',
                    positionIndex: 1
                }
            ),
            covers: new CustomFormControl(
                tableElementSelected?.covers,
                { validators: [Validators.required, Validators.pattern('^[0-9]+$')] },
                {
                    ...new CustomFormControlProps(),
                    label: `TABLES.${isEdit ? 'EDIT_TABLE' : 'ADD_TABLE'}.COVERS`,
                    inputType: 'number',
                    class: '',
                    positionIndex: 3
                }
            ),
            order_type: new CustomFormControl(
                tableElementSelected?.order_type || 'single',
                {},
                {
                    ...new CustomFormControlProps(),
                    label: `TABLES.${isEdit ? 'EDIT_TABLE' : 'ADD_TABLE'}.ORDER_TYPE`,
                    inputType: "radio",
                    inputChoices: orderTypeChoices,
                    class: 'tw-w-full tw-mb-0',
                    componentClass: 'tw-flex-col tw-items-start',
                    positionIndex: 4
                }
            ),
            shapeImage: new CustomFormGroup<CustomForm<ShapeImage>>(
                {
                    table: new CustomFormControl<TableWithIcon<AllTablesShape | AllStaticElementsShape> | undefined>(
                        isTable ? tableElementTypeChosen : undefined,
                        {},
                        {
                            ...new CustomFormControlProps(),
                            label: `TABLES.EDIT_TABLE.TABLES`,
                            inputType: "listOfItems",
                            listItems: tableShapes,
                            class: 'tw-w-full',
                            componentClass: 'tw-flex-nowrap',
                            matElementClass: 'tw-gap-x-8 tw-flex tw-h-18 tw-overflow-auto tw-items-center tw-pr-5',
                            matElementSelected: 'tw-border-2 tw-border-solid tw-border-blue-500 tw-rounded-md',
                            iconClass: 'tw-fill-[#ddd6e2] tw-scale-[2.2] tw-m-0',
                            positionIndex: 0
                        }
                    ),
                    element: new CustomFormControl<TableWithIcon<AllTablesShape | AllStaticElementsShape> | undefined>(
                        isTable ? undefined : tableElementTypeChosen,
                        {},
                        {
                            ...new CustomFormControlProps(),
                            label: `TABLES.EDIT_TABLE.ELEMENTS`,
                            inputType: "listOfItems",
                            listItems: elementsShapes,
                            class: 'tw-w-full',
                            componentClass: 'tw-flex-nowrap',
                            matElementClass: 'tw-gap-x-8 tw-h-18 tw-flex tw-overflow-auto tw-items-center tw-pr-5',
                            matElementSelected: 'tw-border-2 tw-border-solid tw-border-blue-500 tw-rounded-md',
                            iconClass: 'tw-scale-[2.2] tw-m-0',
                            positionIndex: 1
                        }
                    )
                },
                {},
                { ...new CustomFormGroupProps(), positionIndex: 0 }
            )
        });

        this.tableForm = form as CustomFormGroup<CustomForm<AddEditTable>>;

        return form;
    }

    private get shapeImageControl(){return this.tableForm?.controls.shapeImage;}
    private get shapeImageTableControl(){return this.shapeImageControl?.controls.table;}
    private get shapeImageElementControl(){return this.shapeImageControl?.controls.element;}
    @autobind
    private selectTable(tableSelected:TableWithIcon<AllTablesShape>){
        this.shapeImageTableControl?.setValue(tableSelected);
    }
    @autobind
    private selectElement(elementSelected:TableWithIcon<AllStaticElementsShape>){
        this.shapeImageElementControl?.setValue(elementSelected);
    }

    public getTableSizes(tableTypeChosen:TableWithIcon<AllTablesShape|AllStaticElementsShape>){
        return tableTypeChosen.sizes.map(size=>({key:size.dimension,value:size.shape}));
    }

// END - ADD_TABLE FORM

// START - COPY_TABLE FORM
    public duplicateTableForm(tableSelected:RoomsTables, room: Rooms, marginTop:number, marginLeft:number ){
        const roomWidth = room.width * ROOM_MULTIPLICATION_FACTOR;
        const roomHeight = room.height * ROOM_MULTIPLICATION_FACTOR;

        const itemsAvailablePerRow = Math.trunc((roomWidth - tableSelected.xpos-tableSelected.width) / (tableSelected.width + marginLeft));
        const itemsAvailablePerColumn = Math.trunc((roomHeight - tableSelected.ypos-tableSelected.height) / (tableSelected.height + marginTop));

        const itemsPerRowLengths = itemsAvailablePerRow >= 1 ? itemsAvailablePerRow + 1 : 1;
        const itemsPerColumnLengths = itemsAvailablePerColumn >= 1 ? itemsAvailablePerColumn + 1 : 1;

        const rowsChoices:KeyValueIconImage<number>[]=Array.from({length:itemsPerRowLengths},(_,i)=>({key:`${i+1}`,value:i+1}));
        const columnsChoices:KeyValueIconImage<number>[]=Array.from({length:itemsPerColumnLengths},(_,i)=>({key:`${i+1}`,value:i+1}));

        this.copyTableForm=new CustomFormGroup<CustomForm<DuplicateTableForm>>({
            new_name:new CustomFormControl(tableSelected?.name||'',{validators:[Validators.required, Validators.minLength(2),Validators.maxLength(15)]},{...new CustomFormControlProps(),label:`TABLES.DUPLICATE_TABLE.NEW_NAME`,class:'tw-w-full tw-mb-8',hint:()=>`${this.new_name?.value.length||0}/15`}),
            row_elements:new CustomFormControl('',{validators:Validators.required},{...new CustomFormControlProps(),label:`TABLES.DUPLICATE_TABLE.ITEMS_PER_ROW`,class:'tw-w-full',inputType:'select',inputChoices:rowsChoices}),
            column_elements:new CustomFormControl('',{validators:Validators.required},{...new CustomFormControlProps(),label:`TABLES.DUPLICATE_TABLE.ITEMS_PER_COLUMN`,class:'tw-w-full',inputType:'select',inputChoices:columnsChoices}),
        });
        return this.copyTableForm;
    }

    private get new_name(){
        return this.copyTableForm?.controls.new_name;
    }
// END - COPY_TABLE FORM
}


